import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { localToday, matchesInsight } from '../utils/executiveInsights';
import { ActivitySheetMappingDialog } from '../components/ActivitySheetMappingDialog';
import type { SheetMappingPlan, SheetRowResolutions } from '../components/ActivitySheetMappingDialog';
import {
  DataGridAction,
  TaskOutlineCell,
  getStatusTone,
  useSemanticColors,
  GoogleSheetsDownloadIcon,
  ThemedIconButton,
  BulkSelectionBar,
  EditableGrid,
  ExpandCollapseToggle,
  MultiSelectFilter,
  ProgramIconPicker,
  RowDragHandleContext,
  ScheduleDeltaBadge,
  TablePagination,
  TaskHierarchyActionsMenu,
  AssigneeSelector,
  DateRangeFilterDropdown,
  ColumnFilterHeader,
  NotesActionButton,
  NotesModal,
  DependencyPicker,
  GridPriorityBadge,
  GridStatusBadge,
  InlineEditableCell,
  StatsCard,
  PageSkeleton,
  TabBar,
  ThemedCard,
  ThemedInput,
  ThemedText,
  ThemedAlert,
  ManagementButton,
  ReviewOption,
  PageHeader,
  ConfirmModal,
  getProgramIcon,
  useTaskClipboardHotkeys,
  useUserPreferences,
} from '@satsankalpa-foundation-inc/ui';
import type { ColumnDef, DateRangeFilterValue, ExpandedState } from '@satsankalpa-foundation-inc/ui';
import {
  calculateCriticalPath,
  canIndentTask,
  canOutdentTask,
  duplicateTaskHierarchy,
  getTaskIndentTarget,
  getTaskDescendantIds,
  getTaskHierarchySelection,
  getTaskTrackerMetrics,
  isTaskDueThisWeek,
  isTaskOverdue,
  indentTask,
  outdentTask,
  pasteTaskHierarchySelection,
  removeTaskHierarchy,
  reorderTaskAmongSiblings,
  TASK_TRACKER_STATUS_LABELS,
  TASK_TRACKER_STATUSES,
} from '@satsankalpa-foundation-inc/task-domain';
import type { TaskTrackerPriority, TaskTrackerStatus } from '@satsankalpa-foundation-inc/task-domain';
import { AlertTriangle, BriefcaseBusiness, CalendarClock, CheckCircle2, ClipboardCopy, ClipboardPaste, Filter, GitBranch, GripVertical, IndentDecrease, IndentIncrease, ListChecks, Plus, Scissors, Search, Trash2, X } from 'lucide-react';
import { useActivityStore } from '../hooks/useActivityStore';
import { activityCreationScope, activityProgramAssignment, activityProgramName, activityProgramOptions, normalizeProgramFilters } from '../utils/activityPrograms';
import { buildActivityTree, flattenActivityRows } from '../utils/activityTree';
import type { ActivityRow } from '../utils/activityTree';
import type { Activity } from '../types/activity';
import { useAuth } from '../contexts/AuthContext';
import { ImportPreviewDialog } from '@satsankalpa-foundation-inc/ui';
import type { ImportPreview, ImportApplyOptions } from '@satsankalpa-foundation-inc/ui';
import { apiFetch } from '../utils/api';

type FocusFilter = 'all' | 'open' | 'overdue' | 'due-this-week' | 'blocked';

const STATUS_OPTIONS = TASK_TRACKER_STATUSES.map((status) => ({
  value: status,
  label: TASK_TRACKER_STATUS_LABELS[status],
}));

function formatDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function createActivityId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `demo-${crypto.randomUUID()}`
    : `demo-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function FilterPill({ label, onClear }: { label: string; onClear: () => void }) {
  return <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:bg-indigo-800/60 dark:text-indigo-200"><span className="truncate">{label}</span><button type="button" onClick={onClear} aria-label={`Remove ${label} filter`} className="rounded-full p-0.5 hover:bg-indigo-200 dark:hover:bg-indigo-700"><X className="h-2.5 w-2.5" /></button></span>;
}

export default function ActivityTracker() {
  const { user } = useAuth();
  const colors = useSemanticColors();
  const [searchParams, setSearchParams] = useSearchParams();
  const insightDrilldown = Boolean(searchParams.get('insight'));
  const { activities, saveActivities, bulkUpdateActivities, bulkMoveActivities, clearAllActivities, loadedAt, isLoading, isSaving, error, clearError, capabilities, reload } = useActivityStore();
  const [importPreview,setImportPreview] = useState<ImportPreview | null>(null);
  const [sheetMapping,setSheetMapping] = useState<SheetMappingPlan | null>(null);
  const [importBusy,setImportBusy] = useState(false);
  const [importError,setImportError] = useState<string | null>(null);
  const [importMessage,setImportMessage] = useState<string | null>(null);
  const [confirmPull,setConfirmPull] = useState(false);
  const [ignoreInvalidDates,setIgnoreInvalidDates] = useState(false);
  const [clearReview,setClearReview] = useState<{activityCount:number;revision:number;key:string}|null>(null);
  const [clearBusy,setClearBusy] = useState(false);
  const [clearFailure,setClearFailure] = useState<string|null>(null);
  const [clearMessage,setClearMessage] = useState<string|null>(null);
  const [restoreClearFocus,setRestoreClearFocus] = useState(false);
  const clearControlRef = useRef<HTMLSpanElement>(null);
  const pullControlRef = useRef<HTMLSpanElement>(null);
  const reloadControlRef = useRef<HTMLSpanElement>(null);
  const actionPermissionsKnown = typeof capabilities.canClear === 'boolean' && typeof capabilities.canImport === 'boolean';
  const closeClearReview=()=>{
    setClearReview(null);
    setRestoreClearFocus(true);
  };
  // Wait for React to commit enabled controls after the save/reload settles.
  // A one-frame callback can run while both toolbar buttons are still disabled.
  useEffect(()=>{
    if (!restoreClearFocus || clearReview || clearBusy || isSaving || isLoading) return;
    const target=clearControlRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')
      || pullControlRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')
      || reloadControlRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
    if (target) {target.focus();setRestoreClearFocus(false);}
  },[restoreClearFocus,clearReview,clearBusy,isSaving,isLoading,error]);
  const reviewClear=async()=>{
    setClearBusy(true);setClearFailure(null);setClearMessage(null);
    try {
      const summary=await apiFetch('/api/v1/activities/clear-preview') as {activityCount:number;revision:number};
      if (!summary.activityCount) {setClearMessage('The activity tracker is already empty.');await reload();setRestoreClearFocus(true);return;}
      setClearReview({...summary,key:`clear:${crypto.randomUUID()}`});
    } catch(error) {setClearFailure(error instanceof Error?error.message:'Unable to review tracker content');}
    finally {setClearBusy(false);}
  };
  const confirmClear=async()=>{
    if (!clearReview) return;
    setClearBusy(true);
    try {
      const result=await clearAllActivities(clearReview.revision,clearReview.key);
      setSelectedIds(new Set());setActivityClipboard([]);setExpandedRows({});setHighlightedIds(new Set());setPendingFocusId(null);
      setClearMessage(`Cleared ${result.cleared} tracker activit${result.cleared===1?'y':'ies'}.`);
      setClearReview(null);
    } finally {setClearBusy(false);}
  };
  const previewSheet = async (resolutions?:SheetRowResolutions, ignoreDates=sheetMapping?.ignoreInvalidDates ?? ignoreInvalidDates) => {
    setIgnoreInvalidDates(ignoreDates);
    setImportBusy(true); setImportError(null); setImportMessage(null);
    try {
      const input = {ignoreInvalidDates:ignoreDates,...(sheetMapping ? {sourceDigest:sheetMapping.sourceDigest,targetRevision:sheetMapping.targetRevision,...(resolutions?{resolutions}:{})} : {})};
      const result = await apiFetch('/api/v1/activity-imports/preview',{method:'POST',body:JSON.stringify(input)}) as ImportPreview | SheetMappingPlan;
      if ('requiresMapping' in result) {setSheetMapping(result);setImportPreview(null);}
      else {setSheetMapping(null);setImportPreview(result);}
    }
    catch (error) {
      setImportError(error instanceof Error ? error.message:'Unable to preview sheet');
      if (!sheetMapping && !importPreview) requestAnimationFrame(() => pullControlRef.current?.querySelector('button')?.focus());
    }
    finally { setImportBusy(false); }
  };
  const applySheet = async ({removeMissing,acceptedIds}:ImportApplyOptions) => {
    if (!importPreview) return;
    setImportBusy(true); setImportError(null);
    try {
      const result = await apiFetch('/api/v1/activity-imports/apply',{method:'POST',body:JSON.stringify({previewId:importPreview.previewId,removeMissing,acceptedIds})});
      setImportPreview(null); setImportMessage(`Sheet refresh applied: ${result.applied} change${result.applied === 1 ? '' : 's'}.`); await reload();
    } catch (error) { setImportError(error instanceof Error ? error.message:'Unable to apply sheet changes'); }
    finally { setImportBusy(false); }
  };
  const { getTablePreferences, updateTablePreferences, isHydrated: preferencesHydrated } = useUserPreferences();
  const [tab, setTab] = useState<'activities' | 'critical-path'>('activities');
  const [focus, setFocus] = useState<FocusFilter>(() => searchParams.get('insight') ? 'all' : 'open');
  const [query, setQuery] = useState('');
  const [unitFilters, setUnitFilters] = useState<string[]>(() => {
    const program = searchParams.get('program');
    return program ? [program] : [];
  });
  const [regionFilters, setRegionFilters] = useState<string[]>(() => searchParams.get('region') ? [searchParams.get('region')!] : []);
  const [ownerFilters, setOwnerFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [priorityFilters, setPriorityFilters] = useState<string[]>([]);
  const [dueDateFilter, setDueDateFilter] = useState<DateRangeFilterValue | undefined>();
  const [notesActivity, setNotesActivity] = useState<Activity | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activityClipboard, setActivityClipboard] = useState<Activity[]>([]);
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => getTablePreferences('activity-tracker').pageSize || 25);
  const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
  const initializedExpansion = useRef(false);
  useEffect(() => {
    if (!searchParams.get('insight')) return;
    setFocus('all'); setPage(1);
    setUnitFilters(searchParams.get('program') ? [searchParams.get('program')!] : []);
    setRegionFilters(searchParams.get('region') ? [searchParams.get('region')!] : []);
    setQuery(''); setStatusFilters([]); setOwnerFilters([]); setPriorityFilters([]); setDueDateFilter(undefined);
  }, [searchParams]);

  useEffect(() => {
    if (isLoading || initializedExpansion.current) return;
    initializedExpansion.current = true;
    setExpandedRows(Object.fromEntries(
      activities.filter((activity) => activities.some((candidate) => candidate.parentId === activity.id)).map((activity) => [activity.id, true]),
    ));
  }, [activities, isLoading]);

  useEffect(() => {
    if (!preferencesHydrated) return;
    setPageSize(getTablePreferences('activity-tracker').pageSize || 25);
  }, [getTablePreferences, preferencesHydrated]);

  const updateActivity = (id: string, patch: Partial<Activity>) => {
    saveActivities(activities.map((activity) => activity.id === id
      ? { ...activity, ...patch, lastUpdated: new Date().toLocaleDateString('en-CA') }
      : activity));
  };
  const can = (activity: Activity, permission: string) => activity.allowedActions?.includes(permission) === true;
  const selectedActivities = activities.filter((activity) => selectedIds.has(activity.id));
  const canAllSelected = (permission: string) => selectedActivities.length > 0
    && selectedActivities.every((activity) => can(activity, permission));

  const activityBranch = (id: string) => {
    const affected = new Set([id, ...getTaskDescendantIds(activities, id)]);
    return [activities.find(activity => activity.id === id)!, ...activities.filter(activity => activity.id !== id && affected.has(activity.id))];
  };
  const updateActivityProgram = (id: string, name: string) => {
    const branch = activityBranch(id);
    if (!branch[0] || !can(branch[0], 'activity.update') || activityProgramName(branch[0], capabilities.programs) === name) return;
    const targets = branch.map(activity => activityProgramAssignment(activity, name, capabilities.programs || [], capabilities.units, capabilities.updateScopes));
    if (targets.some(target => !target)) return;
    const byId = new Map(branch.map((activity, index) => [activity.id, targets[index]!]));
    saveActivities(activities.map(activity => {
      const target = byId.get(activity.id);
      return target && target.owningUnitId !== activity.owningUnitId
        ? {...activity, ...target, lastUpdated: new Date().toLocaleDateString('en-CA')} : activity;
    }));
  };

  const metrics = useMemo(() => getTaskTrackerMetrics(activities), [activities]);
  const programNames = useMemo(() => Array.from(new Set([
    ...activities.map(activity => activityProgramName(activity, capabilities.programs)),
    ...capabilities.units.filter(unit => capabilities.createScopes.some(scope => scope.regions.length
      && (scope.unitIds.includes('*') || scope.unitIds.includes(unit.id))))
      .filter(unit => unit.type !== 'workspace')
      .map(unit => activityProgramName({owningUnitId:unit.id,owningUnit:unit.name}, capabilities.programs)),
  ])).sort(), [activities, capabilities.programs, capabilities.units, capabilities.createScopes]);
  const programFilters = useMemo(() => normalizeProgramFilters(unitFilters, capabilities.programs, capabilities.units), [unitFilters, capabilities.programs, capabilities.units]);
  const regions = useMemo(() => Array.from(new Set([...activities.map(activity => activity.region),
    ...capabilities.createScopes.flatMap(scope => scope.regions.filter(region => region !== '*'))])).sort(), [activities, capabilities.createScopes]);
  const activityAssignees = (activity: Activity) => activity.assignees?.length ? activity.assignees : activity.owner ? [activity.owner] : [];
  const owners = useMemo(() => Array.from(new Set(activities.flatMap((activity) => activityAssignees(activity)))).sort(), [activities]);
  const hasColumnFilters = insightDrilldown || unitFilters.length + regionFilters.length + ownerFilters.length + statusFilters.length + priorityFilters.length > 0 || Boolean(dueDateFilter?.from || dueDateFilter?.to);

  const clearFilters = () => {
    setSearchParams({});
    setQuery('');
    setUnitFilters([]);
    setRegionFilters([]);
    setOwnerFilters([]);
    setStatusFilters([]);
    setPriorityFilters([]);
    setDueDateFilter(undefined);
    setPage(1);
  };

  const selectFocus = (next: FocusFilter) => {
    if (insightDrilldown) setSearchParams({});
    setFocus(next);
    setStatusFilters([]);
    setPage(1);
  };

  const applyStatusFilters = (next: string[]) => {
    if (insightDrilldown) setSearchParams({});
    setStatusFilters(next);
    if (next.length > 0) setFocus('all');
    setPage(1);
  };

  const focusCreatedActivity = (activity: Activity) => {
    const program = activityProgramName(activity, capabilities.programs);
    const assignees = activityAssignees(activity);
    setUnitFilters(current => !current.length || normalizeProgramFilters(current, capabilities.programs, capabilities.units).includes(program) ? current : []);
    setRegionFilters(current => !current.length || current.includes(activity.region) ? current : []);
    setOwnerFilters(current => !current.length || current.some(owner => owner === '' ? !assignees.length : assignees.includes(owner)) ? current : []);
    setPriorityFilters(current => !current.length || current.includes(activity.priority) ? current : []);
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      for (const key of ['insight', 'basis', 'age', 'activity']) next.delete(key);
      if (next.get('program') && !normalizeProgramFilters([next.get('program')!], capabilities.programs, capabilities.units).includes(program)) next.delete('program');
      if (next.get('region') && next.get('region') !== activity.region) next.delete('region');
      return next;
    }, {replace:true});
    setFocus('open');
    setQuery('');
    setStatusFilters([]);
    setDueDateFilter(undefined);
    setEditingActivityId(activity.id);
    setPendingFocusId(activity.id);
    setHighlightedIds(new Set([activity.id]));
    if (activity.parentId) setExpandedRows((current) => ({ ...(current === true ? {} : current), [activity.parentId!]: true }));
  };

  useEffect(() => {
    if (highlightedIds.size === 0) return;
    const timeout = window.setTimeout(() => setHighlightedIds(new Set()), 2500);
    return () => window.clearTimeout(timeout);
  }, [highlightedIds]);

  const { rows: filtered, matchingCount } = useMemo(() => {
    const insightParams = new URLSearchParams(searchParams);
    // Program/region are ordinary editable column filters after arrival.
    insightParams.delete('program'); insightParams.delete('region');
    const parentIds = new Set(activities.flatMap(activity => activity.parentId ? [activity.parentId] : []));
    const today = localToday();
    const matches = activities.filter((activity) => {
      if (insightDrilldown && !matchesInsight(activity, activities, insightParams, today, parentIds)) return false;
      const needle = query.trim().toLowerCase();
      const assignees = activityAssignees(activity);
      if (needle && !`${activity.title} ${activity.note} ${assignees.join(' ')} ${activity.owningUnit}`.toLowerCase().includes(needle)) return false;
      if (programFilters.length && !programFilters.includes(activityProgramName(activity, capabilities.programs))) return false;
      if (regionFilters.length && !regionFilters.includes(activity.region)) return false;
      if (ownerFilters.length && !ownerFilters.some((owner) => owner === '' ? assignees.length === 0 : assignees.includes(owner))) return false;
      if (statusFilters.length && !statusFilters.includes(activity.status)) return false;
      if (priorityFilters.length && !priorityFilters.includes(activity.priority)) return false;
      if (dueDateFilter?.from && (!activity.dueDate || activity.dueDate < dueDateFilter.from)) return false;
      if (dueDateFilter?.to && (!activity.dueDate || activity.dueDate > dueDateFilter.to)) return false;
      if (focus === 'open' && activity.status === 'done') return false;
      if (focus === 'overdue' && !isTaskOverdue(activity)) return false;
      if (focus === 'due-this-week' && !isTaskDueThisWeek(activity)) return false;
      if (focus === 'blocked' && activity.status !== 'blocked') return false;
      return true;
    });
    const visibleIds = new Set(matches.map((activity) => activity.id));
    const byId = new Map(activities.map((activity) => [activity.id, activity]));
    matches.forEach((activity) => {
      const visited = new Set<string>();
      let parentId = activity.parentId;
      while (parentId && !visited.has(parentId)) {
        visited.add(parentId);
        visibleIds.add(parentId);
        parentId = byId.get(parentId)?.parentId;
      }
    });
    return { rows: activities.filter((activity) => visibleIds.has(activity.id)), matchingCount: matches.length };
  }, [activities, dueDateFilter, focus, ownerFilters, priorityFilters, query, regionFilters, statusFilters, programFilters, capabilities.programs, insightDrilldown, searchParams]);

  const dependencyCount = useMemo(() => activities.reduce((sum, activity) => sum + activity.dependencies.length, 0), [activities]);
  const criticalPath = useMemo(() => calculateCriticalPath(activities.filter((activity) => activity.status !== 'done')), [activities]);

  const candidates = useMemo(() => activities.map((activity) => ({
    id: activity.id,
    title: activity.title,
    group: activity.owningUnit,
    status: activity.status,
  })), [activities]);

  const deleteIds = (ids: Iterable<string>) => {
    saveActivities(removeTaskHierarchy(activities, ids));
    setSelectedIds(new Set());
  };

  const bulkUpdate = (patch: Partial<Activity>) => {
    void bulkUpdateActivities(selectedIds, patch);
    setSelectedIds(new Set());
  };

  const copySelectedActivities = () => {
    const selected = getTaskHierarchySelection(activities, selectedIds).map((activity) => ({
      ...activity,
      assignees: [...(activity.assignees || [])],
      impactedPrograms: [...activity.impactedPrograms],
      dependencies: activity.dependencies.map((dependency) => ({ ...dependency })),
    }));
    if (selected.length === 0) return;
    setActivityClipboard(selected);
    setSelectedIds(new Set());
  };

  const cutSelectedActivities = () => {
    if (!canAllSelected('activity.delete')) return;
    const selected = getTaskHierarchySelection(activities, selectedIds).map((activity) => ({
      ...activity,
      assignees: [...(activity.assignees || [])],
      impactedPrograms: [...activity.impactedPrograms],
      dependencies: activity.dependencies.map((dependency) => ({ ...dependency })),
    }));
    if (selected.length === 0) return;
    setActivityClipboard(selected);
    deleteIds(selectedIds);
  };

  const pasteActivities = () => {
    if (activityClipboard.length === 0 || !capabilities.canCreate) return;
    const visualOrder = flattenActivityRows(buildActivityTree(activities));
    const anchorId = visualOrder.find((activity) => selectedIds.has(activity.id))?.id;
    const result = pasteTaskHierarchySelection(activities, activityClipboard, {
      afterTaskId: anchorId,
      createId: createActivityId,
      mapCopy: (source, { idMap }) => ({
        dependencies: source.dependencies.map((dependency) => ({
          ...dependency,
          taskId: idMap.get(dependency.taskId) || dependency.taskId,
        })),
        lastUpdated: new Date().toLocaleDateString('en-CA'),
        sourceRow: 0,
      }),
    });
    saveActivities(result.tasks);
    setSelectedIds(new Set());
    setHighlightedIds(new Set(result.createdIds));
    if (result.createdRootIds[0]) setPendingFocusId(result.createdRootIds[0]);
    if (result.createdIds.length > result.createdRootIds.length) {
      setExpandedRows((current) => ({
        ...(current === true ? {} : current),
        ...Object.fromEntries(result.createdRootIds.map((id) => [id, true])),
      }));
    }
  };

  useTaskClipboardHotkeys({
    enabled: tab === 'activities' && !clearReview && !clearBusy,
    canCopy: selectedIds.size > 0,
    canCut: canAllSelected('activity.delete'),
    canPaste: activityClipboard.length > 0 && capabilities.canCreate,
    onCopy: copySelectedActivities,
    onCut: cutSelectedActivities,
    onPaste: pasteActivities,
  });

  const duplicateActivity = (activity: Activity) => {
    const result = duplicateTaskHierarchy(activities, activity.id, {
      createId: createActivityId,
      mapCopy: (source, { isRoot, idMap }) => ({
        title: isRoot ? `${source.title} (copy)` : source.title,
        dependencies: source.dependencies.map((dependency) => ({
          ...dependency,
          taskId: idMap.get(dependency.taskId) || dependency.taskId,
        })),
        lastUpdated: new Date().toLocaleDateString('en-CA'),
        sourceRow: 0,
      }),
    });
    saveActivities(result.tasks);
    if (result.createdIds.length) {
      const rootId = result.createdIds[0];
      if (result.createdIds.length > 1) setExpandedRows((current) => ({ ...(current === true ? {} : current), [rootId]: true }));
      setPendingFocusId(rootId);
      setHighlightedIds(new Set(result.createdIds));
    }
  };
  const deleteActivity = (activity: Activity) => {
    const descendantCount = getTaskDescendantIds(activities, activity.id).length;
    const suffix = descendantCount ? ` and ${descendantCount} subactivit${descendantCount === 1 ? 'y' : 'ies'}` : '';
    if (window.confirm(`Delete “${activity.title}”${suffix}?`)) deleteIds([activity.id]);
  };

  const addSubactivity = (parent: Activity) => {
    const child: Activity = {
      ...parent,
      id: createActivityId(),
      parentId: parent.id,
      sortOrder: activities.filter((item) => item.parentId === parent.id)
        .reduce((first, item) => Math.min(first, item.sortOrder ?? 0), 0) - 1,
      title: 'New subactivity',
      status: 'not-started',
      dependencies: [],
      note: '',
      updateHistory: '',
      lastUpdated: new Date().toLocaleDateString('en-CA'),
      sourceRow: 0,
    };
    saveActivities([...activities, child]);
    focusCreatedActivity(child);
  };

  const addSibling = (anchor: Activity) => {
    const siblings = activities
      .filter((activity) => activity.parentId === anchor.parentId)
      .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0));
    const insertAt = Math.max(0, siblings.findIndex((activity) => activity.id === anchor.id) + 1);
    const sibling: Activity = {
      ...anchor,
      id: createActivityId(),
      title: anchor.parentId ? 'New subactivity' : 'New activity',
      status: 'not-started',
      dependencies: [],
      note: '',
      updateHistory: '',
      lastUpdated: new Date().toLocaleDateString('en-CA'),
      sourceRow: 0,
    };
    const ordered = [...siblings];
    ordered.splice(insertAt, 0, sibling);
    const orderById = new Map(ordered.map((activity, index) => [activity.id, index]));
    saveActivities([
      ...activities.map((activity) => orderById.has(activity.id) ? { ...activity, sortOrder: orderById.get(activity.id) } : activity),
      { ...sibling, sortOrder: insertAt },
    ]);
    focusCreatedActivity(sibling);
  };

  const cancelDraftActivity = (id: string) => {
    // Gaps in sortOrder are valid; canceling a draft must not rewrite siblings.
    saveActivities(activities.filter((activity) => activity.id !== id));
    setEditingActivityId(null);
    setPendingFocusId(null);
    setHighlightedIds((current) => new Set(Array.from(current).filter((activityId) => activityId !== id)));
    setSelectedIds((current) => new Set(Array.from(current).filter((activityId) => activityId !== id)));
  };

  const indentActivity = (id: string) => {
    const target = getTaskIndentTarget(activities, id);
    if (!target) return;
    saveActivities(indentTask(activities, id));
    setExpandedRows((current) => ({ ...(current === true ? {} : current), [target.newParentId]: true }));
  };

  const outdentActivity = (id: string) => {
    saveActivities(outdentTask(activities, id));
  };

  const bulkHierarchyUpdate = (operation: 'indent' | 'outdent') => {
    const visualOrder = flattenActivityRows(buildActivityTree(activities))
      .filter((activity) => selectedIds.has(activity.id))
      .map((activity) => activity.id);
    void bulkMoveActivities(visualOrder, operation);
    setSelectedIds(new Set());
  };

  const moveActivity = (sourceId: string, targetId?: string) => {
    if (!targetId) return;
    const reordered = reorderTaskAmongSiblings(activities, sourceId, targetId);
    if (reordered !== activities) saveActivities(reordered);
  };

  const allTreeRows = useMemo(() => buildActivityTree(filtered), [filtered]);
  const pageCount = Math.max(1, Math.ceil(allTreeRows.length / pageSize));
  const treeRows = useMemo(() => allTreeRows.slice((page - 1) * pageSize, page * pageSize), [allTreeRows, page, pageSize]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  useEffect(() => {
    if (!pendingFocusId) return;
    const byId = new Map(activities.map(activity => [activity.id, activity]));
    let root = byId.get(pendingFocusId);
    while (root?.parentId && byId.has(root.parentId)) root = byId.get(root.parentId);
    const index = allTreeRows.findIndex(activity => activity.id === root?.id);
    const targetPage = index < 0 ? page : Math.floor(index / pageSize) + 1;
    if (targetPage !== page) {setPage(targetPage);return;}
    let innerFrame: number | undefined;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        const row = Array.from(document.querySelectorAll<HTMLElement>('[data-task-id]'))
          .find(element => element.dataset.taskId === pendingFocusId);
        (row?.querySelector<HTMLElement>('[data-col-id="activity"]') || row)?.scrollIntoView({behavior:'auto',block:'center',inline:'nearest'});
        setPendingFocusId(null);
      });
    });
    return () => {cancelAnimationFrame(outerFrame);if (innerFrame != null) cancelAnimationFrame(innerFrame);};
  }, [pendingFocusId, activities, allTreeRows, page, pageSize]);
  const draggableRowIds = useMemo(
    () => filtered.filter((activity) => can(activity, 'activity.update')).map((activity) => activity.id),
    [filtered],
  );
  const expandableIds = useMemo(() => activities.filter((activity) => activities.some((candidate) => candidate.parentId === activity.id)).map((activity) => activity.id), [activities]);
  const allExpanded = expandedRows === true || (expandableIds.length > 0 && expandableIds.every((id) => Boolean(expandedRows[id])));

  const RowDragHandle = () => {
    const dragContext = useContext(RowDragHandleContext);
    if (!dragContext) return null;
    return (
      <div
        ref={dragContext.setActivatorNodeRef}
        {...dragContext.listeners}
        style={colors(dragContext.isDragging ? 'active' : 'neutral').textStyle}
        className={`flex h-5 w-5 items-center justify-center rounded cursor-grab active:cursor-grabbing ${dragContext.isDragging ? 'text-indigo-600' : 'text-slate-300 hover:text-slate-500 dark:text-slate-500'}`}
        title="Drag to reorder"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
    );
  };

  const columns: ColumnDef<ActivityRow>[] = [
    {
      id: 'order', header: '#', size: 90,
      cell: ({ row }) => {
        return <TaskOutlineCell label={row.original.displayIndex} depth={row.depth} tone={getStatusTone(row.original.status)}
          expandable={Boolean(row.original.subRows?.length)} expanded={row.getIsExpanded()} onToggle={() => row.toggleExpanded()} prefix={<RowDragHandle />} />;
      },
    },
    {
      id: 'activity', accessorKey: 'title', header: 'Activity', size: 310,
      cell: ({ row }) => <div className="flex min-w-0 items-center gap-2">
        {!row.original.parentId && <ProgramIconPicker tone={getStatusTone(row.original.status)} value={activityProgramName(row.original, capabilities.programs)} options={activityProgramOptions(activityBranch(row.original.id), capabilities.programs || [], capabilities.units, capabilities.updateScopes)} disabled={!can(row.original, 'activity.update')} onChange={(value) => updateActivityProgram(row.original.id, value)} />}
        <InlineEditableCell
          value={row.original.title}
          onSave={(value) => { setEditingActivityId(null); updateActivity(row.original.id, { title: value }); }}
          onCancel={editingActivityId === row.original.id ? () => cancelDraftActivity(row.original.id) : undefined}
          autoEdit={editingActivityId === row.original.id}
          saveUnchanged={editingActivityId === row.original.id}
          disabled={!can(row.original, 'activity.update')}
          disabledReason="You do not have edit access for this program or service."
        />
      </div>,
    },
    {
      id: 'start', accessorKey: 'startDate', header: 'Start', size: 110,
      cell: ({ row }) => <InlineEditableCell disabled={!can(row.original, 'activity.update')} value={row.original.startDate || ''} type="date" displayValue={formatDate(row.original.startDate)} onSave={(value) => updateActivity(row.original.id, { startDate: value || null })} />,
    },
    {
      id: 'due', accessorKey: 'dueDate', header: () => <div className="flex items-center justify-center gap-1"><span>End</span><DateRangeFilterDropdown label="End" value={dueDateFilter} onChange={setDueDateFilter} /></div>, size: 120,
      cell: ({ row }) => <InlineEditableCell disabled={!can(row.original, 'activity.update')} value={row.original.dueDate || ''} type="date" displayValue={row.original.scheduleMode === 'ongoing' ? 'Ongoing' : formatDate(row.original.dueDate)} onSave={(value) => updateActivity(row.original.id, { dueDate: value || null, scheduleMode: value ? 'dated' : 'unscheduled' })} />,
    },
    {
      id: 'schedule', header: 'Days', size: 90,
      cell: ({ row }) => <ScheduleDeltaBadge dueDate={row.original.dueDate} isComplete={row.original.status === 'done'} />,
    },
    {
      id: 'status', accessorKey: 'status', header: () => <ColumnFilterHeader label="Status" selected={statusFilters} onChange={applyStatusFilters} allLabel="All" allCount={activities.length} options={TASK_TRACKER_STATUSES.map((item) => ({ value: item, label: TASK_TRACKER_STATUS_LABELS[item], count: activities.filter((activity) => activity.status === item).length }))} />, size: 145,
      cell: ({ row }) => <GridStatusBadge disabled={!can(row.original, 'activity.transition')} status={row.original.status} options={STATUS_OPTIONS} onChange={(status) => updateActivity(row.original.id, { status: status as TaskTrackerStatus })} />,
    },
    {
      id: 'owner', header: () => <ColumnFilterHeader label="Assignees" selected={ownerFilters} onChange={setOwnerFilters} options={[{ value: '', label: 'Unassigned' }, ...owners.map((item) => ({ value: item, label: item }))]} />, size: 215,
      cell: ({ row }) => <AssigneeSelector disabled={!can(row.original, 'activity.assign')} value={activityAssignees(row.original)} options={owners} showFirstNameOnly onChange={(assignees) => updateActivity(row.original.id, { assignees, owner: assignees[0] || '' })} />,
    },
    {
      id: 'region', accessorKey: 'region', header: () => <ColumnFilterHeader label="Region" selected={regionFilters} onChange={setRegionFilters} options={regions.map((item) => ({ value: item, label: item, count: activities.filter((activity) => activity.region === item).length }))} />, size: 115,
      cell: ({ row }) => <InlineEditableCell disabled={!can(row.original, 'activity.update')} value={row.original.region} type="select" options={regions} onSave={(value) => updateActivity(row.original.id, { region: value })} />,
    },
    {
      id: 'priority', accessorKey: 'priority', header: () => <ColumnFilterHeader label="Priority" selected={priorityFilters} onChange={setPriorityFilters} options={(['high', 'medium', 'low'] as TaskTrackerPriority[]).map((item) => ({ value: item, label: item.charAt(0).toUpperCase() + item.slice(1), count: activities.filter((activity) => activity.priority === item).length }))} />, size: 110,
      cell: ({ row }) => <GridPriorityBadge disabled={!can(row.original, 'activity.update')} priority={row.original.priority} onChange={(value) => updateActivity(row.original.id, { priority: value as TaskTrackerPriority })} />,
    },
    {
      id: 'dependencies', header: 'Deps', size: 150,
      cell: ({ row }) => <DependencyPicker disabled={!can(row.original, 'activity.update')} taskId={row.original.id} candidates={candidates} value={row.original.dependencies} onChange={(dependencies) => updateActivity(row.original.id, { dependencies })} />,
    },
    {
      id: 'actions', header: 'Actions', size: 140,
      cell: ({ row }) => <div className="flex items-center justify-center gap-0.5">
        <NotesActionButton notes={row.original.note} onClick={() => setNotesActivity(row.original)} />
        {can(row.original, 'activity.delete') && <DataGridAction icon={<Trash2 className="h-3.5 w-3.5" />} label="Delete activity" variant="danger" onClick={() => deleteActivity(row.original)} />}
        {can(row.original, 'activity.update') && <TaskHierarchyActionsMenu isSubtask={Boolean(row.original.parentId)} onInsertAfter={() => addSibling(row.original)} onAddSubtask={() => addSubactivity(row.original)} onDuplicate={() => duplicateActivity(row.original)} onIndent={() => indentActivity(row.original.id)} onOutdent={() => outdentActivity(row.original.id)} canIndent={canIndentTask(activities, row.original.id)} canOutdent={canOutdentTask(activities, row.original.id)} />}
      </div>,
    },
  ];

  const addActivity = () => {
    if (isSaving || isLoading || importBusy || clearBusy) return;
    const scope = activityCreationScope(capabilities.units, capabilities.programs || [], capabilities.createScopes, programFilters, regionFilters, regions);
    if (!scope) return;
    const {unit:selectedUnit, region:allowedRegion} = scope;
    const activity: Activity = {
      id: createActivityId(),
      title: 'New activity',
      owningUnitId: selectedUnit.id,
      owningUnit: selectedUnit.name,
      functionType: '',
      region: allowedRegion,
      countryScope: allowedRegion,
      owner: ownerFilters[0] || '',
      assignees: ownerFilters[0] ? [ownerFilters[0]] : [],
      startDate: null,
      dueDate: null,
      scheduleMode: 'unscheduled',
      status: 'not-started',
      priority: (priorityFilters[0] as TaskTrackerPriority) || 'medium',
      impact: '',
      note: '',
      updateHistory: '',
      lastUpdated: new Date().toLocaleDateString('en-CA'),
      impactedPrograms: [],
      dependencies: [],
      durationDays: 1,
      parentId: null,
      // Insert before the first root without serial writes to every other row.
      sortOrder: activities.filter((item) => !item.parentId)
        .reduce((first, item) => Math.min(first, item.sortOrder ?? 0), 0) - 1,
      sourceRow: 0,
    };
    saveActivities([...activities, activity]);
    setPage(1);
    focusCreatedActivity(activity);
  };

  return (
    <div className="page-content operations-workspace-page activity-tracker-page space-y-5" aria-busy={isLoading}>
      <PageHeader title="Activity Tracker" actions={<div className="flex flex-col items-end gap-1">
        {user?.role === 'admin' && <div role="group" aria-label="Tracker actions" aria-busy={!actionPermissionsKnown || isLoading || isSaving || importBusy || clearBusy} className="flex h-12 min-w-[104px] items-center justify-end gap-2">
          {actionPermissionsKnown && capabilities.canClear && <span ref={clearControlRef} className="inline-flex"><ThemedIconButton icon={Trash2} title="Clear activity tracker" tone="danger" size="lg"
            loading={clearBusy} disabled={!activities.length || isLoading || isSaving || importBusy} onClick={()=>{void reviewClear();}} /></span>}
          {actionPermissionsKnown && capabilities.canImport && <span ref={pullControlRef} className="inline-flex"><ThemedIconButton customIcon={<GoogleSheetsDownloadIcon className="h-6 w-6" themed />} onClick={() => {setIgnoreInvalidDates(false);setConfirmPull(true);}} loading={isLoading || importBusy} disabled={isSaving || clearBusy} title="Pull from Google Sheets" size="lg" /></span>}
        </div>}
        <ThemedText variant="muted" className="min-h-4 text-right text-xs" role="status" aria-label="Tracker status">
          {isLoading ? '' : isSaving ? 'Saving…' : error ? 'Not synchronized' : ''}
        </ThemedText>
      </div>} />
      <ConfirmModal isOpen={Boolean(clearReview)} onClose={closeClearReview} onConfirm={confirmClear}
        title="Clear activity tracker?" confirmLabel="Clear all activities" variant="danger"
        message={`Permanently remove all ${clearReview?.activityCount || 0} tracker activities across every program and region, including records hidden by filters, their notes and dependency links? Accounts, assignments, audit history and the Google Sheet are kept. This cannot be undone here.`} />
      {clearFailure && <ThemedAlert variant="error" dismissLabel="Dismiss clear error" onDismiss={()=>{setClearFailure(null);closeClearReview();}}>{clearFailure}</ThemedAlert>}
      {clearMessage && <ThemedAlert variant="success" dismissLabel="Dismiss clear notification" onDismiss={()=>setClearMessage(null)}>{clearMessage}</ThemedAlert>}
      <ConfirmModal isOpen={confirmPull} onClose={() => setConfirmPull(false)}
        onConfirm={() => { setConfirmPull(false); void previewSheet(); }}
        title="Pull from Google Sheets?" confirmLabel="Preview changes" variant="warning"
        message="Read the configured Google Sheet and preview its changes? Tracker data stays unchanged until you review and apply the changes.">
        <ReviewOption label="Ignore invalid date fields" checked={ignoreInvalidDates} onChange={setIgnoreInvalidDates}
          description="Keep saved dates for matched activities; leave invalid dates blank for new activities. Other validation errors still require correction." />
      </ConfirmModal>

      {importError && !importPreview && !sheetMapping && <ThemedAlert variant="error" dismissLabel="Dismiss Google Sheets error"
        onDismiss={() => { setImportError(null); pullControlRef.current?.querySelector('button')?.focus(); }}>{importError}</ThemedAlert>}
      {importMessage && <ThemedAlert variant="success" dismissLabel="Dismiss Google Sheets notification" onDismiss={() => {setImportMessage(null);pullControlRef.current?.querySelector('button')?.focus();}}>{importMessage}</ThemedAlert>}
      {importPreview && <ImportPreviewDialog key={importPreview.previewId} preview={importPreview} onClose={() => { setImportPreview(null); setImportError(null); }} onApply={options => { void applySheet(options); }} isApplying={importBusy} error={importError} />}
      {sheetMapping && <ActivitySheetMappingDialog key={`${sheetMapping.sourceDigest}:${sheetMapping.targetRevision}:${Boolean(sheetMapping.ignoreInvalidDates)}`} plan={sheetMapping}
        onClose={()=>{setSheetMapping(null);setImportError(null);}} onReview={resolutions=>{void previewSheet(resolutions);}}
        onIgnoreInvalidDates={value=>{void previewSheet(undefined,value);}} busy={importBusy} error={importError} />}

      {error && <ThemedAlert variant="error" onDismiss={actionPermissionsKnown ? clearError : undefined} dismissLabel="Dismiss activity error">
        <p>{error}</p><span ref={reloadControlRef} className="mt-2 inline-flex"><ManagementButton variant="secondary" size="sm" onClick={()=>{void reload();}} disabled={isLoading || isSaving}>Retry loading activities</ManagementButton></span>
      </ThemedAlert>}

      {isLoading ? <PageSkeleton variant="table" showHeader={false} statCount={6}
        statsClassName="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" /> : loadedAt !== null && <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatsCard label="Total" value={metrics.total} icon={ListChecks} accentColor="slate" onClick={() => selectFocus('all')} isActive={focus === 'all' && statusFilters.length === 0} />
        <StatsCard label="Open" value={metrics.open} icon={CalendarClock} accentColor="blue" onClick={() => selectFocus('open')} isActive={focus === 'open' && statusFilters.length === 0} />
        <StatsCard label="Done" value={metrics.done} icon={CheckCircle2} status="success" onClick={() => applyStatusFilters(['done'])} isActive={statusFilters.length === 1 && statusFilters[0] === 'done'} />
        <StatsCard label="Overdue" value={metrics.overdue} icon={AlertTriangle} status="danger" onClick={() => selectFocus('overdue')} isActive={focus === 'overdue' && statusFilters.length === 0} />
        <StatsCard label="Due this week" value={metrics.dueThisWeek} icon={CalendarClock} status="warning" onClick={() => selectFocus('due-this-week')} isActive={focus === 'due-this-week' && statusFilters.length === 0} />
        <StatsCard label="Blocked" value={metrics.blocked} icon={AlertTriangle} status="danger" onClick={() => selectFocus('blocked')} isActive={focus === 'blocked' && statusFilters.length === 0} />
      </div>

      <TabBar
        tabs={[
          { id: 'activities', label: 'Activities', icon: ListChecks },
          { id: 'critical-path', label: 'Dependencies & Critical Path', count: dependencyCount, icon: GitBranch },
        ]}
        activeTab={tab}
        onTabChange={(value) => setTab(value as 'activities' | 'critical-path')}
      />

      {tab === 'activities' ? (
        <>
          {hasColumnFilters && (
            <div className="flex flex-wrap items-center gap-1.5 border-b border-indigo-100 bg-indigo-50/80 px-4 py-1.5 text-xs text-indigo-700 dark:border-slate-700 dark:bg-indigo-900/30 dark:text-indigo-300">
              <Filter className="h-3 w-3 shrink-0" />
              {insightDrilldown && <FilterPill label={`Overview: ${searchParams.get('activity') ? 'Selected activity' : searchParams.get('age') ? `${searchParams.get('age')} days overdue` : searchParams.get('insight')?.replaceAll('-', ' ')} · ${searchParams.get('basis') === 'delivery' ? 'Delivery items' : 'All activities'}`} onClear={() => setSearchParams({})} />}
              {insightDrilldown && <span>{matchingCount} matching · parents shown for context</span>}
              {programFilters.length > 0 && <FilterPill label={`Program: ${programFilters.slice(0, 2).join(', ')}${programFilters.length > 2 ? ` +${programFilters.length - 2}` : ''}`} onClear={() => setUnitFilters([])} />}
              {regionFilters.length > 0 && <FilterPill label={`Region: ${regionFilters.join(', ')}`} onClear={() => setRegionFilters([])} />}
              {ownerFilters.length > 0 && <FilterPill label={`Assignee: ${ownerFilters.slice(0, 1).join(', ')}${ownerFilters.length > 1 ? ` +${ownerFilters.length - 1}` : ''}`} onClear={() => setOwnerFilters([])} />}
              {statusFilters.length > 0 && <FilterPill label={`Status: ${statusFilters.map((item) => TASK_TRACKER_STATUS_LABELS[item as TaskTrackerStatus]).join(', ')}`} onClear={() => setStatusFilters([])} />}
              {priorityFilters.length > 0 && <FilterPill label={`Priority: ${priorityFilters.join(', ')}`} onClear={() => setPriorityFilters([])} />}
              {dueDateFilter && <FilterPill label={`Due: ${dueDateFilter.from || '…'} – ${dueDateFilter.to || '…'}`} onClear={() => setDueDateFilter(undefined)} />}
              <span className="tabular-nums">{filtered.length} results</span>
              <button type="button" onClick={clearFilters} className="ml-auto text-[11px] font-semibold underline underline-offset-2">Clear all</button>
            </div>
          )}
          <EditableGrid
            data={treeRows}
            columns={columns}
            storageKey="activity-tracker"
            getSubRows={(row) => row.subRows}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            highlightedIds={highlightedIds}
            expandedRows={expandedRows}
            onExpandedChange={setExpandedRows}
            draggableRowIds={draggableRowIds}
            onRowOrderChange={moveActivity}
            emptyState={<div className="py-12 text-center text-sm text-slate-500">{error ? 'Activities could not be loaded' : 'No activities match these filters'}</div>}
            getRowStyle={(activity) => colors(isTaskOverdue(activity) ? 'danger' : getStatusTone(activity.status)).rowStyle}
            toolbar={<div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3"><span className="text-xl">🪷</span><div><div className="text-base font-bold text-slate-800 dark:text-slate-100">Activity Planning</div><div className="text-xs text-slate-500">{activities.filter((item) => !item.parentId).length} activities · {activities.filter((item) => item.parentId).length} subactivities · {metrics.done} done</div></div></div>
              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
                <div className="relative min-w-[12rem] max-w-xs flex-1 sm:flex-none">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <ThemedInput aria-label="Search activities" placeholder="Search activities..." value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} className="!h-8 w-full !rounded-lg !py-1 !pl-8 !text-xs sm:w-56" />
                </div>
                {(query || hasColumnFilters) && <button type="button" onClick={clearFilters} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-indigo-600 dark:hover:bg-slate-700" aria-label="Clear search and filters" title="Clear search and filters"><X className="h-3.5 w-3.5" /></button>}
                <MultiSelectFilter
                  icon={<BriefcaseBusiness className="h-3.5 w-3.5" />}
                  label="Program / Service"
                  title="Filter programs and services"
                  options={programNames.map((unit) => {
                    const ProgramIcon = getProgramIcon(unit);
                    return { id: unit, label: unit, leading: <span style={colors('neutral').textStyle}><ProgramIcon className="h-3.5 w-3.5 shrink-0" /></span> };
                  })}
                  selected={new Set(programFilters)}
                  onToggle={(unit) => { setUnitFilters(programFilters.includes(unit) ? programFilters.filter((item) => item !== unit) : [...programFilters, unit]); setPage(1); }}
                  onClear={() => { setUnitFilters([]); setPage(1); }}
                  onOnly={(unit) => { setUnitFilters([unit]); setPage(1); }}
                />
                <TablePagination
                  page={page}
                  pageCount={pageCount}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => { setPageSize(size); updateTablePreferences('activity-tracker', { pageSize: size }); setPage(1); }}
                />
                <ExpandCollapseToggle isExpanded={allExpanded} onToggle={() => setExpandedRows(allExpanded ? {} : true)} />
                {capabilities.canCreate && <ThemedIconButton icon={Plus} onClick={addActivity} title="Add activity" size="sm" loading={isSaving} disabled={isLoading || importBusy || clearBusy} />}
              </div>
            </div>}
            toggleableColumns={[
              { id: 'start', label: 'Start Date' }, { id: 'due', label: 'End Date' }, { id: 'schedule', label: 'Days' },
              { id: 'status', label: 'Status' }, { id: 'owner', label: 'Assignees' }, { id: 'region', label: 'Region' }, { id: 'priority', label: 'Priority' },
              { id: 'dependencies', label: 'Dependencies' },
            ]}
          />
          {selectedIds.size > 0 && <BulkSelectionBar
            selectedCount={selectedIds.size}
            totalCount={filtered.length}
            allSelected={selectedIds.size === filtered.length && filtered.length > 0}
            onToggleAll={(checked) => setSelectedIds(checked ? new Set(filtered.map((activity) => activity.id)) : new Set())}
            variant="floating"
            actionSections={[
              { id: 'clipboard', label: 'Clipboard', items: [
                { id: 'copy', label: 'Copy', shortcut: '⌘/Ctrl+C', icon: ClipboardCopy, onClick: copySelectedActivities },
                { id: 'cut', label: 'Cut', shortcut: '⌘/Ctrl+X', icon: Scissors, disabled: !canAllSelected('activity.delete'), onClick: cutSelectedActivities },
                { id: 'paste', label: activityClipboard.length ? `Paste (${activityClipboard.length})` : 'Paste', shortcut: '⌘/Ctrl+V', icon: ClipboardPaste, disabled: activityClipboard.length === 0 || !capabilities.canCreate, onClick: pasteActivities },
              ] },
              { id: 'status', label: 'Set Status', items: [
                ...TASK_TRACKER_STATUSES.map((status) => ({ id: status, label: TASK_TRACKER_STATUS_LABELS[status], disabled: !canAllSelected('activity.transition'), onClick: () => bulkUpdate({ status }) })),
              ] },
              { id: 'priority', label: 'Set Priority', items: [
                { id: 'low', label: 'Low', disabled: !canAllSelected('activity.update'), onClick: () => bulkUpdate({ priority: 'low' }) },
                { id: 'medium', label: 'Medium', disabled: !canAllSelected('activity.update'), onClick: () => bulkUpdate({ priority: 'medium' }) },
                { id: 'high', label: 'High', disabled: !canAllSelected('activity.update'), onClick: () => bulkUpdate({ priority: 'high' }) },
              ] },
              { id: 'hierarchy', label: 'Hierarchy', items: [
                { id: 'indent', label: 'Indent (make subactivity)', icon: IndentIncrease, disabled: !canAllSelected('activity.update'), onClick: () => bulkHierarchyUpdate('indent') },
                { id: 'outdent', label: 'Outdent (promote)', icon: IndentDecrease, disabled: !canAllSelected('activity.update'), onClick: () => bulkHierarchyUpdate('outdent') },
              ] },
              { id: 'danger', items: [{ id: 'delete', label: 'Delete selected', icon: Trash2, variant: 'danger', disabled: !canAllSelected('activity.delete'), onClick: () => { if (window.confirm(`Delete ${selectedIds.size} selected activities and their subactivities?`)) deleteIds(selectedIds); } }] },
            ]}
          />}
        </>
      ) : (
        <ThemedCard className="space-y-5">
          <div>
            <ThemedText variant="h3">Dependency and critical-path preview</ThemedText>
            <ThemedText variant="muted">Dependencies can be added directly in the Activities table. The spreadsheet did not contain dependency links, so none are inferred.</ThemedText>
          </div>
          {dependencyCount === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 dark:border-slate-600">
              Add one or more dependencies to open activities, then return here to see the calculated path.
            </div>
          ) : criticalPath.hasCycle ? (
            <div style={colors('danger').badgeStyle} className="rounded-xl p-4 text-sm">
              Circular dependency detected across {criticalPath.cycleTaskIds.length} activities. Remove one of the links before calculating the path.
            </div>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-3">
                <StatsCard label="Dependency links" value={dependencyCount} icon={GitBranch} />
                <StatsCard label="Critical activities" value={criticalPath.criticalTaskIds.length} icon={AlertTriangle} />
                <StatsCard label="Calculated duration" value={`${criticalPath.projectDurationDays} days`} icon={CalendarClock} />
              </div>
              <div className="space-y-2">
                {criticalPath.tasks.filter((item) => item.isCritical).map((item, index) => {
                  const activity = activities.find((candidate) => candidate.id === item.id);
                  return (
                    <div key={item.id} style={colors('warning').badgeStyle} className="flex items-center gap-3 rounded-lg border px-4 py-3">
                      <span style={{backgroundColor:colors('warning').fill,color:colors('warning').onFill}} className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold">{index + 1}</span>
                      <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{item.title}</div><div className="text-xs text-slate-500">{activity?.owningUnit} · {item.durationDays} days · zero slack</div></div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </ThemedCard>
      )}
      </>}
      <NotesModal
        isOpen={Boolean(notesActivity)}
        title={notesActivity?.title || ''}
        initialNotes={notesActivity?.note || ''}
        currentUser={user?.name || user?.email || 'Operations User'}
        readOnly={notesActivity ? !can(notesActivity, 'activity.update') : true}
        onSave={async (notes) => {
          if (notesActivity) updateActivity(notesActivity.id, { note: notes });
        }}
        onClose={() => setNotesActivity(null)}
      />
    </div>
  );
}
